import { inject, Injectable } from '@angular/core'
import { map, Observable } from 'rxjs'
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http'

export type FileUploadType = 'agenda' | 'recording' | 'custom-document' | 'board-pack'

const uploadErrorMessages: Record<string, string> = {
  PASSWORD_PROTECTED_PDF: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.PASSWORD_PROTECTED_PDF',
  TOO_MANY_PAGES: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.TOO_MANY_PAGES',
  DUPLICATE_RESOURCE_NAME: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.DUPLICATE_FILE_NAME',
}

export function getUploadErrorMessage(error: any): string | null {
  return uploadErrorMessages[error?.error?.message] ?? null
}

export type UploadProgress = {
  progress: number
  uploadKey: string | null
  remainingTimeSeconds: number | null
}

@Injectable({
  providedIn: 'root',
})
export class UploadsService {
  http = inject(HttpClient)

  uploadCustomDocument(chosenFile: File): Observable<UploadProgress> {
    return this.upload('custom-document', chosenFile)
  }

  uploadAgenda(chosenFile: File): Observable<UploadProgress> {
    return this.upload('agenda', chosenFile)
  }

  uploadRecording(chosenFile: File): Observable<UploadProgress> {
    return this.upload('recording', chosenFile)
  }

  upload(type: FileUploadType, chosenFile: File): Observable<UploadProgress> {
    const formData: FormData = new FormData()
    formData.append('file', chosenFile)
    const startTime = Date.now()
    return this.http
      .post(`/api/uploads/${type}`, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0
              const remainingTimeSeconds = this.calculateRemainingTime(startTime, event.loaded, event.total ?? null)
              return { progress, uploadKey: null, remainingTimeSeconds }
            case HttpEventType.Response:
              return { progress: 100, uploadKey: event.body.uploadKey, remainingTimeSeconds: null }
            default:
              return { progress: 0, uploadKey: null, remainingTimeSeconds: null }
          }
        })
      )
  }

  private calculateRemainingTime(startTime: number, loaded: number, total: number | null): number | null {
    if (total === null || loaded === 0) {
      return null
    }
    const elapsedTimeSeconds = (Date.now() - startTime) / 1000
    if (elapsedTimeSeconds < 0.5) {
      return null
    }
    const bytesPerSecond = loaded / elapsedTimeSeconds
    const remainingBytes = total - loaded
    const remainingTimeSeconds = Math.ceil(remainingBytes / bytesPerSecond)
    return remainingTimeSeconds
  }
}
